<script setup>
import { ref, onMounted, computed } from "vue";
import { useCompraBoletosStore } from "@/store/CompraBoletosStore";
import { getTiposBoleto, getPeleadoresDisponibles } from "@/utils/api_evento";
import TerminosYCondicionesEvento from "@/components/Evento-Julio/Boletos/TerminosYCondiciones.vue";
import Swal from "sweetalert2";

const emit = defineEmits(["next-step", "prev-step"]);
const store = useCompraBoletosStore();

const boletos = ref([]);
const peleadores = ref([]);
const cargando = ref(true);
const mostrarTerminos = ref(false);
const dropdownAbierto = ref(false);

onMounted(async () => {
  try {
    const [b, p] = await Promise.all([getTiposBoleto(), getPeleadoresDisponibles()]);
    boletos.value = b;
    peleadores.value = p;

    // ✅ FIX: si ya hay un boleto seleccionado del store (rehidratado),
    // sincronizar el precioUnitario desde la respuesta fresca del API
    if (store.boleto.idBoleto) {
      const boletoActual = b.find((x) => x.id === store.boleto.idBoleto);
      if (boletoActual) {
        if (boletoActual.disponible === false) {
          // El boleto que traía guardado ya se agotó (ej. otra pestaña,
          // sesión vieja) — limpiamos la selección en vez de dejarlo pasar.
          store.boleto.idBoleto = null;
          store.boleto.precioUnitario = 0;
          store.boleto.cantidad = 1;
          Swal.fire({
            icon: "info",
            title: "Ese boleto ya se agotó",
            text: "Selecciona otro tipo de boleto para continuar.",
            confirmButtonColor: "#007bff",
          });
        } else {
          store.boleto.precioUnitario = parseFloat(boletoActual.precio) || 0;
        }
      }
    }
  } catch (error) {
    console.error("Error cargando datos:", error);
  } finally {
    cargando.value = false;
  }
});

const boletoSeleccionado = computed(
  () => boletos.value.find((b) => b.id === store.boleto.idBoleto) ?? null
);

// ✅ FIX: totalCompra usa boletoSeleccionado.precio (fuente de verdad del API)
// en lugar de store.boleto.precioUnitario que puede no estar seteado correctamente
const totalCompra = computed(() => {
  if (!boletoSeleccionado.value) return 0;
  return boletoSeleccionado.value.precio * store.boleto.cantidad;
});

const formatPrecio = (v) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v ?? 0);

const accentTipo = (tipo) =>
  ({
    general: "#00aaff",
    primera_fila: "#00ccaa",
    mesa_vip: "#ffaa00",
    streaming: "#aa55ff",
  }[tipo] ?? "#00aaff");

const iconoTipo = (tipo) =>
  ({
    general: "bi-ticket-perforated",
    primera_fila: "bi-star",
    mesa_vip: "bi-gem",
    streaming: "bi-play-circle",
  }[tipo] ?? "bi-ticket");

function seleccionarBoleto(boleto) {
  // ✅ FIX: no permitir seleccionar un boleto agotado (cupo cubierto).
  if (boleto.disponible === false) return;
  store.setBoleto(boleto); // ya hace parseFloat internamente
  store.boleto.cantidad = 1;
  dropdownAbierto.value = false;
}

function decrementarCantidad() {
  if (store.boleto.cantidad > 1) store.boleto.cantidad--;
}

function incrementarCantidad() {
  if (store.boleto.cantidad < maxCantidad.value) store.boleto.cantidad++;
}

const maxCantidad = computed(() => {
  const maximoCompra = boletoSeleccionado.value?.maximo_por_compra ?? 20;
  const disponibles = boletoSeleccionado.value?.boletos_disponibles;
  // boletos_disponibles === null significa sin límite (cupo null en el back)
  if (disponibles === null || disponibles === undefined) return maximoCompra;
  return Math.max(Math.min(maximoCompra, disponibles), 0);
});

const cintaColor = (cinta) =>
  ({
    blanca: "#888",
    azul: "#0044ee",
    morada: "#7a00cc",
    cafe: "#5c3010",
    negra: "#111111",
    verde: "#007a00",
    amarilla: "#b89000",
    amarilla_naranja: "#e87500",
    naranja: "#e85c00",
    naranja_verde: "#4a8c00",
    blanca_amarilla: "#9a8a00",
  }[cinta] ?? "#888");

// Colores de texto legibles sobre cada cinta
const cintaTextColor = (cinta) => {
  const claros = ["blanca", "amarilla", "amarilla_naranja"];
  return claros.includes(cinta) ? "rgba(0,0,0,0.85)" : "rgba(255,255,255,0.95)";
};

const CINTA_ORDEN = {
  negra: 0,
  cafe: 1,
  morada: 2,
  azul: 3,
  blanca: 4,
  verde: 5,
  naranja_verde: 6,
  naranja: 7,
  amarilla_naranja: 8,
  amarilla: 9,
  blanca_amarilla: 10,
};
const peleadoresOrdenados = computed(() =>
  [...peleadores.value].sort(
    (a, b) => (CINTA_ORDEN[a.cinta] ?? 99) - (CINTA_ORDEN[b.cinta] ?? 99)
  )
);

function seleccionarPeleador(p) {
  if (store.boleto.peleadorId === p.id) {
    // Toggle: deseleccionar si ya está seleccionado
    store.boleto.peleadorId = null;
    store.boleto.peleadorNombre = "";
  } else {
    store.boleto.peleadorId = p.id;
    store.boleto.peleadorNombre = `${p.nombre} ${p.apellido}`;
  }
}

function continuar() {
  if (!store.boleto.idBoleto) {
    Swal.fire({
      icon: "warning",
      title: "Selecciona un boleto",
      text: "Elige el tipo de boleto antes de continuar.",
      confirmButtonColor: "#007bff",
    });
    return;
  }
  if (boletoSeleccionado.value?.disponible === false) {
    Swal.fire({
      icon: "error",
      title: "Boleto agotado",
      text: "Este tipo de boleto ya no está disponible. Elige otro para continuar.",
      confirmButtonColor: "#007bff",
    });
    return;
  }
  if (!store.boleto.peleadorId) {
    Swal.fire({
      icon: "info",
      title: "¿A quién vas a ver? 🥋",
      text: "Es necesario seleccionar a tu peleador favorito para continuar.",
      confirmButtonColor: "#007bff",
      confirmButtonText: "Entendido",
    });
    return;
  }
  if (!store.aceptaTerminos) {
    Swal.fire({
      icon: "info",
      title: "Términos y condiciones",
      text: "Debes aceptar los términos para continuar.",
      confirmButtonColor: "#007bff",
    });
    return;
  }
  emit("next-step");
}
</script>

<template>
  <!-- Vista términos -->
  <div v-if="mostrarTerminos">
    <TerminosYCondicionesEvento />
    <div class="text-end mt-3">
      <button class="btn-nav btn-secondary-nav" @click="mostrarTerminos = false">
        <i class="bi bi-arrow-left"></i> Volver al formulario
      </button>
    </div>
  </div>

  <!-- Vista formulario -->
  <form v-else class="step-container" @submit.prevent="continuar">
    <div v-if="cargando" class="estado-centro">
      <div class="spinner">
        <div></div>
        <div></div>
        <div></div>
      </div>
    </div>

    <template v-else>
      <!-- ══ FILA ESCRITORIO: Boleto | Cantidad | Total en una sola línea ══ -->
      <!-- En móvil: boleto ocupa fila completa; cantidad+total comparten segunda fila -->
      <div class="seleccion-layout mb-16">
        <!-- Tipo de boleto -->
        <div class="boleto-col">
          <label class="field-label">Tipo de boleto <span class="req">*</span></label>
          <div
            class="dropdown-boleto"
            :class="{ 'dropdown-boleto--open': dropdownAbierto }"
          >
            <button
              type="button"
              class="dropdown-trigger"
              :style="
                boletoSeleccionado
                  ? { '--accent': accentTipo(boletoSeleccionado.tipo) }
                  : {}
              "
              @click="dropdownAbierto = !dropdownAbierto"
            >
              <span v-if="boletoSeleccionado" class="trigger-seleccionado">
                <i
                  :class="['bi', iconoTipo(boletoSeleccionado.tipo), 'trigger-icono']"
                  :style="{ color: accentTipo(boletoSeleccionado.tipo) }"
                ></i>
                <span class="trigger-nombre">{{ boletoSeleccionado.nombre }}</span>
                <span class="trigger-precio">{{
                  formatPrecio(boletoSeleccionado.precio)
                }}</span>
              </span>
              <span v-else class="trigger-placeholder">
                <i class="bi bi-ticket-perforated"></i>
                Selecciona un tipo de boleto
              </span>
              <i class="bi bi-chevron-down dropdown-chevron"></i>
            </button>

            <div v-if="dropdownAbierto" class="dropdown-opciones">
              <div
                v-for="boleto in boletos"
                :key="boleto.id"
                class="dropdown-opcion"
                :class="{
                  'dropdown-opcion--selected': store.boleto.idBoleto === boleto.id,
                  'dropdown-opcion--agotado': boleto.disponible === false,
                }"
                :style="{ '--accent': accentTipo(boleto.tipo) }"
                @click="seleccionarBoleto(boleto)"
              >
                <div class="opcion-left">
                  <i :class="['bi', iconoTipo(boleto.tipo), 'opcion-icono']"></i>
                  <div class="opcion-info">
                    <span class="opcion-nombre">
                      {{ boleto.nombre }}
                      <span v-if="boleto.disponible === false" class="badge-agotado"
                        >AGOTADO</span
                      >
                    </span>
                    <span class="opcion-desc">
                      {{
                        boleto.disponible === false
                          ? "Ya no hay boletos disponibles de este tipo"
                          : boleto.mensaje_promocional || boleto.descripcion
                      }}
                    </span>
                  </div>
                </div>
                <div class="opcion-right">
                  <span class="opcion-precio">{{ formatPrecio(boleto.precio) }}</span>
                  <i
                    v-if="store.boleto.idBoleto === boleto.id"
                    class="bi bi-check-circle-fill opcion-check"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Cantidad -->
        <div v-if="boletoSeleccionado" class="cantidad-col">
          <label class="field-label">Cantidad <span class="req">*</span></label>
          <div class="cantidad-control">
            <button
              type="button"
              class="cantidad-btn"
              :disabled="store.boleto.cantidad <= 1"
              @click="decrementarCantidad"
            >
              −
            </button>
            <span class="cantidad-valor">{{ store.boleto.cantidad }}</span>
            <button
              type="button"
              class="cantidad-btn"
              :disabled="store.boleto.cantidad >= maxCantidad"
              @click="incrementarCantidad"
            >
              +
            </button>
          </div>
        </div>

        <!-- Total -->
        <div v-if="boletoSeleccionado" class="total-col">
          <div class="total-badge">
            <span class="total-label">Total</span>
            <span class="total-monto">{{ formatPrecio(totalCompra) }}</span>
          </div>
        </div>
      </div>

      <!-- ══ PELEADORES ══ -->
      <div class="field-group mb-16">
        <label class="field-label"> ¿A qué peleador vas a ver? </label>

        <div v-if="peleadores.length" class="peleadores-grid">
          <div
            v-for="p in peleadoresOrdenados"
            :key="p.id"
            class="peleador-card"
            :class="{ 'peleador-card--selected': store.boleto.peleadorId === p.id }"
            @click="seleccionarPeleador(p)"
          >
            <!-- Foto -->
            <div class="peleador-foto-wrap">
              <img
                :src="p.foto"
                :alt="`${p.nombre} ${p.apellido}`"
                class="peleador-foto"
                @error="$event.target.src = '/img/usuario.png'"
              />
              <i
                v-if="store.boleto.peleadorId === p.id"
                class="bi bi-check-circle-fill peleador-check-icon"
              ></i>
            </div>

            <!-- Info -->
            <div class="peleador-body">
              <span class="peleador-nombre">{{ p.nombre }} {{ p.apellido }}</span>
              <span class="peleador-equipo">{{ p.equipo }}</span>
            </div>

            <!-- Cinta -->
            <div
              class="peleador-cinta"
              :style="{
                background: cintaColor(p.cinta),
                color: cintaTextColor(p.cinta),
              }"
            >
              {{ p.cinta_display }}
            </div>
          </div>
        </div>
        <p v-else class="hint-msg">No hay peleadores disponibles aún.</p>
      </div>

      <!-- ══ TÉRMINOS ══ -->
      <div class="terminos-row">
        <label class="terminos-label">
          <input type="checkbox" v-model="store.aceptaTerminos" class="terminos-check" />
          <span>
            He leído y acepto los
            <a href="#" @click.prevent="mostrarTerminos = true" class="terminos-link">
              Términos y Condiciones
            </a>
          </span>
        </label>
      </div>
    </template>

    <!-- Navegación -->
    <div class="nav-row">
      <button type="button" class="btn-nav btn-secondary-nav" @click="$emit('prev-step')">
        <i class="bi bi-arrow-left"></i><span class="btn-text">Atrás</span>
      </button>
      <a href="/" class="btn-nav btn-ghost-nav">
        <i class="bi bi-house-fill"></i><span class="btn-text">Inicio</span>
      </a>
      <button type="submit" class="btn-nav btn-primary-nav" :disabled="cargando">
        <span class="btn-text">Siguiente</span><i class="bi bi-arrow-right"></i>
      </button>
    </div>
  </form>
</template>

<style scoped>
.step-container {
  padding: 0.5rem 0;
}
.mb-16 {
  margin-bottom: 16px;
}
.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #444;
}
.field-label-opt {
  font-weight: 400;
  color: #aaa;
  font-size: 0.75rem;
}
.req {
  color: #dc3545;
}
.hint-msg {
  font-size: 0.7rem;
  color: #aaa;
}

/* ══ Layout selección: escritorio 1 fila / móvil 2 filas ══ */
.seleccion-layout {
  display: grid;
  /* Desktop: boleto flexible, cantidad fija, total flexible */
  grid-template-columns: 1fr auto 1fr;
  grid-template-areas: "boleto cantidad total";
  align-items: end;
  gap: 14px;
}
.boleto-col {
  grid-area: boleto;
}
.cantidad-col {
  grid-area: cantidad;
}
.total-col {
  grid-area: total;
}

/* Móvil: boleto fila 1 completo; cantidad+total fila 2 */
@media (max-width: 600px) {
  .seleccion-layout {
    grid-template-columns: auto 1fr;
    grid-template-areas:
      "boleto   boleto"
      "cantidad total";
    gap: 12px;
  }
  .total-col {
    min-width: 0;
  }
}

/* ── Dropdown boleto ── */
.dropdown-boleto {
  position: relative;
}
.dropdown-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1.5px solid #d8d8d8;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-size: 0.9rem;
  text-align: left;
  min-height: 44px;
}
.dropdown-trigger:hover,
.dropdown-boleto--open .dropdown-trigger {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}
.trigger-seleccionado {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.trigger-icono {
  font-size: 1.1rem;
  flex-shrink: 0;
}
.trigger-nombre {
  font-weight: 700;
  color: #1a1a2e;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.trigger-precio {
  font-weight: 700;
  color: var(--accent, #007bff);
  font-size: 0.95rem;
  white-space: nowrap;
  flex-shrink: 0;
}
.trigger-placeholder {
  color: #aaa;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  font-size: 0.88rem;
}
.dropdown-chevron {
  color: #aaa;
  font-size: 0.8rem;
  transition: transform 0.2s;
  flex-shrink: 0;
  margin-left: 6px;
}
.dropdown-boleto--open .dropdown-chevron {
  transform: rotate(180deg);
}
.dropdown-opciones {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1.5px solid #d8d8d8;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 100;
  overflow: hidden;
}
.dropdown-opcion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f4f4f4;
  transition: background 0.15s;
  gap: 10px;
}
.dropdown-opcion:last-child {
  border-bottom: none;
}
.dropdown-opcion:hover {
  background: color-mix(in srgb, var(--accent) 6%, #fff);
}
.dropdown-opcion--selected {
  background: color-mix(in srgb, var(--accent) 8%, #fff);
}
.dropdown-opcion--agotado {
  cursor: not-allowed;
  opacity: 0.55;
}
.dropdown-opcion--agotado:hover {
  background: transparent;
}
.dropdown-opcion--agotado .opcion-icono {
  color: #aaa;
}
.badge-agotado {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #f1f1f1;
  color: #999;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  vertical-align: middle;
}
.opcion-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}
.opcion-icono {
  font-size: 1.2rem;
  color: var(--accent);
  flex-shrink: 0;
}
.opcion-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.opcion-nombre {
  font-weight: 700;
  font-size: 0.88rem;
  color: #1a1a2e;
}
.opcion-desc {
  font-size: 0.7rem;
  color: #888;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.opcion-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.opcion-precio {
  font-weight: 800;
  font-size: 1rem;
  color: var(--accent);
}
.opcion-check {
  color: var(--accent);
  font-size: 1rem;
}

/* ── Cantidad ── */
.cantidad-col {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.cantidad-control {
  display: flex;
  align-items: center;
  border: 1.5px solid #d8d8d8;
  border-radius: 8px;
  overflow: hidden;
  width: fit-content;
}
.cantidad-btn {
  width: 38px;
  height: 44px;
  border: none;
  background: #f4f4f4;
  font-size: 1.15rem;
  font-weight: 700;
  cursor: pointer;
  color: #333;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cantidad-btn:hover:not(:disabled) {
  background: #e0e0e0;
}
.cantidad-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.cantidad-valor {
  min-width: 44px;
  text-align: center;
  font-weight: 700;
  font-size: 1.05rem;
  color: #1a1a2e;
  line-height: 44px;
}

/* ── Total ── */
.total-badge {
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: #f0f7ff;
  border: 1px solid #c8e0ff;
  border-radius: 10px;
  padding: 10px 14px;
  height: 44px;
  min-width: 130px;
  justify-content: center;
}
/* En móvil, el total ocupa toda la celda */
@media (max-width: 600px) {
  .total-badge {
    height: auto;
    padding: 10px 14px;
    width: 100%;
  }
}
.total-label {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #555;
  line-height: 1;
}
.total-monto {
  font-size: 1.4rem;
  font-weight: 800;
  color: #007bff;
  line-height: 1.1;
}
.total-detalle {
  font-size: 0.68rem;
  color: #888;
  line-height: 1;
}

/* ══ Peleadores — grid de tarjetas ══ */
.peleadores-grid {
  display: grid;
  /* Mínimo 160px, llenan el espacio disponible — en escritorio quedan 3-4 columnas */
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 2px;
  scrollbar-width: thin;
  scrollbar-color: #c8d8f0 transparent;
}
.peleadores-grid::-webkit-scrollbar {
  width: 4px;
}
.peleadores-grid::-webkit-scrollbar-thumb {
  background: #c8d8f0;
  border-radius: 4px;
}

.peleador-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px 10px;
  border-radius: 10px;
  border: 1.5px solid #e8e8e8;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, transform 0.15s;
  text-align: center;
  position: relative;
  background: #fff;
}
.peleador-card:hover {
  background: #f5faff;
  border-color: #b8d8ff;
  transform: translateY(-1px);
}
.peleador-card--selected {
  background: #e8f3ff;
  border-color: #007bff;
}

.peleador-foto-wrap {
  position: relative;
  flex-shrink: 0;
}
.peleador-foto {
  width: 65px;
  height: 65px;
  border-radius: 15%;
  object-fit: cover;
  object-position: center top;
  border: 2px solid #e8e8e8;
  display: block;
}
.peleador-card--selected .peleador-foto {
  border-color: #007bff;
}
.peleador-check-icon {
  position: absolute;
  bottom: -2px;
  right: -4px;
  color: #007bff;
  font-size: 1rem;
  background: #fff;
  border-radius: 50%;
  line-height: 1;
}

.peleador-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}
.peleador-nombre {
  font-weight: 700;
  font-size: 0.8rem;
  color: #1a1a2e;
  /* Nombre en 2 líneas máx para no truncar en móvil */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.25;
}
.peleador-equipo {
  font-size: 0.66rem;
  color: #888;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
}

.peleador-cinta {
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 20px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  width: 100%;
  text-align: center;
}

/* ── Móvil: foto izquierda + nombre derecha / cinta segunda fila completa ── */
@media (max-width: 600px) {
  .peleadores-grid {
    grid-template-columns: 1fr;
  }
  .peleador-card {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    grid-template-areas:
      "foto info"
      "cinta cinta";
    text-align: left;
    padding: 10px 12px;
    gap: 0 12px;
    align-items: center;
  }
  .peleador-foto-wrap {
    grid-area: foto;
    align-self: center;
  }
  .peleador-foto {
    width: 72px;
    height: 72px;
  }
  .peleador-body {
    grid-area: info;
    align-self: center;
  }
  .peleador-nombre {
    font-size: 0.88rem;
  }
  .peleador-cinta {
    grid-area: cinta;
    margin-top: 8px;
  }
}

/* ── Términos ── */
.terminos-row {
  margin: 4px 0 16px;
}
.terminos-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #444;
}
.terminos-check {
  margin-top: 2px;
  width: 16px;
  height: 16px;
  accent-color: #007bff;
  flex-shrink: 0;
}
.terminos-link {
  color: #007bff;
  text-decoration: underline;
}

/* ── Spinner ── */
.estado-centro {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}
.spinner {
  position: relative;
  width: 36px;
  height: 36px;
}
.spinner div {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: #007bff;
  animation: giro 1s linear infinite;
}
.spinner div:nth-child(2) {
  inset: 6px;
  border-top-color: rgba(0, 123, 255, 0.4);
  animation-duration: 1.5s;
  animation-direction: reverse;
}
.spinner div:nth-child(3) {
  inset: 12px;
  border-top-color: rgba(0, 123, 255, 0.2);
  animation-duration: 2s;
}
@keyframes giro {
  to {
    transform: rotate(360deg);
  }
}

/* ── Navegación ── */
.nav-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
.btn-nav {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: all 0.2s ease;
}
.btn-primary-nav {
  background: #007bff;
  color: #fff;
}
.btn-primary-nav:hover {
  background: #0060df;
}
.btn-primary-nav:disabled {
  background: #b0c8f0;
  cursor: not-allowed;
}
.btn-secondary-nav {
  background: #f0f0f0;
  color: #555;
}
.btn-secondary-nav:hover {
  background: #e0e0e0;
}
.btn-ghost-nav {
  background: transparent;
  color: #888;
  border: 1.5px solid #ddd;
}
.btn-ghost-nav:hover {
  border-color: #bbb;
  color: #555;
}

@media (max-width: 375px) {
  .btn-text {
    display: none;
  }
  .btn-nav {
    justify-content: center;
    padding: 9px 14px;
  }
}
</style>
